<!-- eslint-disable @typescript-eslint/no-magic-numbers -->
<script lang="ts" setup>
    import BackButton from '@/components/BackButton.ce.vue';
    import { DEFAULT_IMG } from '@/constants/image';
    import { formatCurrency } from '@/helpers/formatting';
    import { type EventTypeTyping, type SpaceTyping } from '@/typings/widget';
    import AdvancedImage from '@shared/components/ui/advanced-image.vue';
    import { combineDescriptions } from '@shared/helpers/notes';
    import { formatTextToHtml } from '@shared/helpers/text';

    const props = defineProps<{
        modelValue: number[],
        items: SpaceTyping[]
        isInPath?: boolean
    }>();

    // eslint-disable-next-line func-call-spacing
    const emit = defineEmits<{
        (event: 'update:modelValue', value: number[]): void
        (event: 'next' | 'goBack'): void
    }>();

    const { t } = useI18n();

    // Stores
    const { venue, venueEventTypes } = storeToRefs(useWidgetVenueStore());
    const { eventTypeId } = storeToRefs(useWidgetPathStore());

    // Refs
    const overlayTarget: Ref<HTMLElement | null> = ref(null);
    const sliderWrapper: Ref<HTMLElement | null> = ref(null);
    const sliderSize: Ref<number[]> = ref([0, 0]);
    const currentIndex: Ref<number> = ref(0);
    const currentNotes: Ref<HTMLElement | null> = ref(null);
    const selectedModel: Ref<Record<number, boolean>> = ref({});

    // Computed
    const totalSelected = computed((): number => Object.values(selectedModel.value)
        .filter((value: boolean) => value).length);
    const selectedEventType: ComputedRef<EventTypeTyping | undefined > = computed(() => venueEventTypes.value.find(eventType => eventType.id === eventTypeId.value));

    // Methods
    const abbrByStrategy = (space: SpaceTyping | null): string | null => {
        if (!space) return null;
        return t(`widget.global.pricing-strategy.${space.pricingStrategy}`);
    };

    const handleContinue = async(): Promise<void> => {
        await nextTick(() => { emit('next') });
    };

    const calculateSlider = (): void => {
        if (sliderWrapper.value) {
            sliderSize.value = [sliderWrapper.value.clientWidth, sliderWrapper.value.clientWidth / 2];
        }
    };

    const cleanup = (): void => {
        window.removeEventListener('resize', calculateSlider);
        overlayTarget.value = null;
        sliderWrapper.value = null;
        sliderSize.value = [0, 0];
        currentIndex.value = 0;
        currentNotes.value = null;
    };

    const toArrayValue = (currentValue: Record<number, boolean>): number[] => {
        const result: number[] = [];

        Object.keys(currentValue)
            .forEach((key: string) => {
                if (currentValue[Number(key)]) result.push(Number(key));
            });

        return result;
    };

    const toObjectValue = (items: { id: number }[], value: number[]): Record<number, boolean> => {
        const result: Record<number, boolean> = {};

        items.forEach((item: { id: number }) => {
            result[item.id] = value.includes(item.id);
        });

        return result;
    };

    const init = (): void => {
        selectedModel.value = toObjectValue(props.items, props.modelValue);
    };

    onMounted(() => {
        calculateSlider();
        window.addEventListener('resize', calculateSlider);
        init();
    });

    onBeforeMount(() => { cleanup() });

    watch(
        selectedModel, (nVal: Record<number, boolean>) => {
            emit('update:modelValue', toArrayValue(nVal));
        }, { deep: true },
    );
</script>

<template>
    <div class="space-selection">
        <div
            v-if="isInPath"
            class="space-selection__header"
        >
            <BackButton
                :on-click="() => emit('goBack')"
            />
        </div>
        <div class="space-selection__content">
            <h2 class="space-selection__title">
                {{ t('widget.quote.space.title') }}
            </h2>
            <p class="space-selection__subtitle">
                {{ t('widget.quote.space.subtitle', { event: selectedEventType?.name }) }}
            </p>
            <div class="space-selection__spaces">
                <div
                    v-for="space in props.items"
                    :key="space.id"
                    :class="{'space-selection__space--selected': selectedModel[space.id]}"
                    class="space-selection__space"
                    @click="selectedModel[space.id] = !selectedModel[space.id]"
                >
                    <AdvancedImage
                        aspect-ratio="2:1"
                        class="space-selection__space-img"
                        :media="space.cover"
                        :preview="space.cover ? null : DEFAULT_IMG"
                    />
                    <div class="space-selection__space-content">
                        <span class="space-selection__space-title">
                            <strong>
                                {{ space.name }}
                            </strong>
                            <span v-if="venue?.showProductPrices">
                                <strong>
                                    {{ formatCurrency(space.price) }}
                                </strong>
                                {{ abbrByStrategy(space) }}
                            </span>
                        </span>
                        <span
                            v-if="space.notes.length"
                            v-safe-html="formatTextToHtml(combineDescriptions(space.notes))"
                            class="space-selection__space-description"
                        />
                        <div class="space-selection__space-features">
                            <div class="space-selection__space-feature">
                                <label>{{ t('widget.global.labels.capacity') }}</label>
                                <span>{{ space.minCapacity }} - {{ space.maxCapacity }} {{ t('widget.global.people') }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <button
                type="button"
                class="btn space-selection__action"
                :class="{
                    'btn--primary': totalSelected,
                    'btn--secondary': !totalSelected,
                }"
                @click="handleContinue"
            >
                <span v-if="totalSelected">
                    {{ t('widget.quote.space.confirm') }} ({{ totalSelected }})
                </span>
                <span v-else>
                    {{ t('widget.quote.space.continue') }}
                </span>
            </button>
        </div>
    </div>
</template>

<style scoped lang="scss">
.space-selection {
    position: relative;
    display: flex;
    flex-direction: column;
    inline-size: 100%;
    min-block-size: 100%;

    &__subtitle {
        text-align: center;
        text-transform: lowercase;

        &::first-letter {
            text-transform: uppercase;
        }
    }

    &__content {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding-block: 0 var(--spacing-16);
        padding-inline: var(--spacing-03);

        @include min-width(lg) {
            padding-inline: var(--spacing-07);
        }
    }

    &__header {
        inline-size: 100%;
        line-height: 1;
        margin-block: var(--spacing-04) 0;
        margin-inline: 0;
        padding-block: 0;
        padding-inline: var(--widget-inner-gutter);
    }

    &__spaces {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-03);
        margin-block-start: var(--spacing-06);
    }

    &__space {
        display: flex;
        flex-direction: column;
        padding: var(--spacing-03);
        border: 1px solid var(--border-tertiary);
        border-radius: var(--border-radius-md);
        background-color: rgb(255 255 255 / 0.4);
        cursor: pointer;
        gap: var(--spacing-05);
        inline-size: 100%;
        transition: all 0.2s ease;

        @include min-width(lg) {
            flex-direction: row;
        }

        &:hover:not(.space-selection__space--selected) {
            background-color: var(--background-secondary-highlight);
            transform: translateY(-2px);
        }

        &--selected {
            border-color: var(--border-primary);
            background-color: var(--background-primary-highlight);
        }

        &-img {
            border-radius: var(--border-radius-sm);

            @include min-width(lg) {
                flex: 0.4;
            }
        }

        &-content,
        &-features,
        &-feature {
            display: flex;
            flex-direction: column;
        }

        &-content,
        &-features {
            gap: var(--spacing-03);
        }

        &-content {
            @include min-width(lg) {
                flex: 0.6;
                gap: var(--spacing-05);
            }
        }

        &-features {
            @include min-width(lg) {
                flex-direction: row;
            }
        }

        &-title {
            display: flex;
            align-items: center;
            justify-content: space-between;
            font-size: var(--font-large);
        }

        &-description {
            color: var(--text-secondary);
        }

        &-feature {
            display: flex;
            flex-direction: column;

            label {
                display: inline-block;
                padding: 0;
                color: var(--text-secondary);
                font-size: var(--font-regular);
                font-weight: var(--font-weight-light);
                margin-block-end: var(--spacing-01);
            }

            span {
                display: flex;
                align-items: center;
                justify-content: center;
                padding: var(--spacing-03);
                border-radius: var(--border-radius-sm);
                background-color: var(--background-primary);
                block-size: var(--size-6);
                font-size: var(--font-regular);
            }
        }
    }

    &__action {
        position: fixed;
        z-index: var(--zindex-stack-twice);
        align-self: center;
        inset-block-end: var(--spacing-05);
        inset-inline-start: 50%;
        min-inline-size: 174px;
        transform: translateX(-50%);

        @include min-width(lg) {
            inset-block-end: var(--spacing-04);
            inset-inline: auto var(--spacing-04);
            margin-block-end: 0;
            transform: none;
        }
    }
}
</style>
